import { cn } from "@fieldcheck/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * FieldCheck brand mark + wordmark.
 *
 * Identity: a rugged inspection "field plate" — a clipboard silhouette stamped
 * with a bold verification check, the signature mark of an on-site inspector.
 *
 * THEME-SAFE BY CONSTRUCTION (gotcha confirmed in sibling clones): drawing mark
 * parts with `text-accent` / `stroke-warning` / raw accent on the page surface
 * makes them vanish under pale/amber custom themes. Instead the entire mark is
 * drawn with `currentColor` inside a SOLID `bg-accent` chip whose text color is
 * `text-accent-foreground` — a pair the theme engine GUARANTEES to contrast.
 * The wordmark uses `text-foreground` (always legible over the app background),
 * so it never depends on the accent hue for legibility.
 */
function BrandMark(props: { px: number }) {
  const glyph = () => Math.round(props.px * 0.62);
  return (
    <div
      class="grid place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5"
      style={{ width: `${props.px}px`, height: `${props.px}px` }}
      aria-hidden="true"
    >
      {/* Clipboard plate + verification check, drawn entirely with currentColor
       * (inherited from text-accent-foreground) so contrast is guaranteed. */}
      <svg
        width={glyph()}
        height={glyph()}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.1"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="shrink-0"
      >
        {/* Clipboard body */}
        <path d="M6.5 4.5h11a1.5 1.5 0 0 1 1.5 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19V6a1.5 1.5 0 0 1 1.5-1.5Z" />
        {/* Clip at the top */}
        <path d="M9.25 4.5a2.75 2.75 0 0 1 5.5 0" fill="currentColor" stroke="none" />
        <path d="M9.25 4.5a2.75 2.75 0 0 1 5.5 0" />
        {/* Bold inspection check inside the plate */}
        <path d="M8.5 12.5l2.6 2.6L15.5 9.8" stroke-width="2.6" />
      </svg>
    </div>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <BrandMark px={32} />
          <span class="text-xl font-extrabold tracking-tight text-foreground">
            Field<span class="font-semibold text-muted-foreground">Check</span>
          </span>
        </>
      ) : (
        <>
          <div class="mb-2 transition-transform hover:scale-105 active:scale-95 duration-300">
            <BrandMark px={64} />
          </div>
          <h1 class="text-3xl font-extrabold tracking-tight text-foreground">
            Field<span class="text-muted-foreground">Check</span>
          </h1>
        </>
      )}
    </div>
  );
}
